/**
 * ONE constructor for every synchronous refusal `AgentApiClient.request()` hands
 * back (F-LIVE2-40, final review Important #1).
 *
 * `StrategyOrchestrator.createWithAIServer` compensates — deletes the document it
 * created seconds earlier — on a CERTAIN refusal, and it reads that certainty off
 * `httpStatus`. The billing-blocked branch used to build its own literal without
 * the field, so a 402 (the shape a DEV run hits first) left the orphan wave 9 W1
 * exists to end. Both branches now go through this builder, so a failure shape
 * that carries no status is impossible to write by accident.
 *
 * Pure and dependency-free on purpose: `AgentApiClient` itself cannot be loaded by
 * any CommonJS test runtime (`import.meta.env` in its constructor), so this is the
 * piece a unit test can actually pin.
 */
import type { AgentApiErrorResponse } from './api.types';

export interface AgentApiRefusalInput<TBillingBlock> {
  /** The HTTP status of the answer that refused — always a real status. */
  status: number;
  /** The server's own reason, already unwrapped from the payload. */
  error: string;
  /**
   * The parsed billing block, when the refusal is a billing one. Kept opaque:
   * shared-types must not depend on `billing-access`.
   */
  billingBlock?: TBillingBlock;
}

/**
 * Builds the `AgentApiErrorResponse` for an HTTP answer that refused, keeping any
 * billing block beside it (the global billing dialog still opens on it).
 */
export function agentApiRefusal<TBillingBlock = unknown>(
  input: AgentApiRefusalInput<TBillingBlock>,
): AgentApiErrorResponse & { billingBlock?: TBillingBlock } {
  return {
    success: false,
    error: input.error,
    httpStatus: input.status,
    ...(input.billingBlock !== undefined ? { billingBlock: input.billingBlock } : {}),
  };
}
